import * as R from "ramda";
import * as React from "react";
import { FaCircle, FaRegCircle, FaRegSquare, FaSquare } from "react-icons/fa";
import { HTMLEditorComponent } from "ts/components/course-author/html-editor/Editor.component";
import { PreferredLocale } from "../../../../../../../../utils/locales";
import { HTMLEditor } from "../../../../../../../course-author/html-editor/Editor.container";
import { HTMLRenderer } from "../../../../../../../course-author/html-editor/Renderer";
import { IHTMLEditorProps } from "../../../../../../../course-author/html-editor/types";
import {
  AnswerBox,
  AnswerContent,
  ClickableText,
  HintBox,
  HoverableAnswerBox,
} from "../../../../../../../course-author/instruction/parts/exercises/FormatElements";
import { IChoiceExercise, ILocalizedChoiceExerciseItem } from "../../../../../../../course-author/types";
import ToggleDisplay from "../../../../../../../utils/ToggleDisplay";
import { ControlBar } from "../../../ControlBar";
import { isSingleChoicePart } from "../../../util";

const changedChoiceOrLocale = (props: IChoiceItemProps, oldProps: IChoiceItemProps): boolean =>
  props.choice !== oldProps.choice || props.currentLocale !== oldProps.currentLocale;

interface ISelectedProps {
  selected: boolean;
  singleChoice: boolean;
}

export const SelectedIcon: React.SFC<ISelectedProps> = ({ selected, singleChoice }) =>
  singleChoice ? selected ? <FaCircle /> : <FaRegCircle /> : selected ? <FaSquare /> : <FaRegSquare />;

export interface IChoiceItemProps {
  choiceExercise: IChoiceExercise;
  canEdit: boolean;
  choice: ILocalizedChoiceExerciseItem;
  currentLocale: PreferredLocale;
  deleteItem: () => void;
  modifyHint: (hint: string, currentLocale?: PreferredLocale) => void;
  modifyText: (text: string, currentLocale?: PreferredLocale) => void;
  toggleCorrect: () => void;
}

interface IChoiceItemState {
  editHint: boolean;
  editText: boolean;
  hint: Nullable<string>;
  text: Nullable<string>;
}

export class ChoiceItem extends React.Component<IChoiceItemProps, IChoiceItemState> {
  public hintContainerRef = React.createRef<HTMLDivElement>();
  public textContainerRef = React.createRef<HTMLDivElement>();
  private readonly defaultHint = "Empty hint";
  private editorRef = React.createRef<HTMLEditorComponent>();

  constructor(props: IChoiceItemProps) {
    super(props);
    this.state = {
      editHint: false,
      editText: false,
      hint: R.pathOr(null, ["hint", "value", "text"], props.choice),
      text: R.pathOr(null, ["text", "value", "text"], props.choice),
    };
  }
  public componentDidUpdate(oldProps: IChoiceItemProps) {
    if (changedChoiceOrLocale(this.props, oldProps)) {
      this.setState({
        hint: R.pathOr(null, ["hint", "value", "text"], this.props.choice),
        text: R.pathOr(null, ["text", "value", "text"], this.props.choice),
      });
    }
  }
  public saveContent = () => {
    const editor = this.editorRef.current;
    if (R.isNil(editor)) {
      return;
    } else {
      const formatedContent = editor.getFormatedEditorContent(undefined, this.props.currentLocale);
      this.editText(formatedContent, this.props.currentLocale);
    }
  };
  public toggleEditHint = () => this.setState({ editHint: !this.state.editHint });
  public toggleEditText = () => this.setState({ editText: !this.state.editText });

  public editHint = (hint: string, currentLocale: PreferredLocale) => {
    if (hint !== this.getHint()) {
      this.setState({ hint });
      this.props.modifyHint(hint, currentLocale);
    }
    this.toggleEditHint();
  };

  public editText = (text: string, currentLocale: PreferredLocale) => {
    if (text !== this.getText()) {
      this.setState({ text });
      this.props.modifyText(text, currentLocale);
    }
    this.toggleEditText();
  };
  public getHint = () => this.state.hint || "";
  public getText = () => this.state.text || "";

  public render() {
    const { canEdit, choice, currentLocale } = this.props;
    const singleChoice = isSingleChoicePart(this.props.choiceExercise);
    return (
      <div>
        <ToggleDisplay shouldDisplay={!canEdit}>
          <AnswerBox>
            <SelectedIcon selected={choice.correct} singleChoice={singleChoice} />
            <AnswerContent>
              <HTMLRenderer source={this.state.text} />
            </AnswerContent>
          </AnswerBox>
        </ToggleDisplay>
        <ToggleDisplay shouldDisplay={canEdit}>
          <HintBox>
            <AnswerContent>
              <ClickableText shouldDisplay={!this.state.editHint} onClick={this.toggleEditHint}>
                <HTMLRenderer source={this.state.hint || this.defaultHint} />
              </ClickableText>
              {this.state.editHint && (
                <div ref={this.hintContainerRef}>
                  <HTMLEditor
                    initialContent={this.state.hint || ""}
                    currentLocale={currentLocale}
                    getContent={this.getHint}
                    setContent={this.editHint}
                    shouldAutoSave={this.editorShouldAutoSave("editHint")}
                    shouldResetState={this.editorShouldResetState}
                    shouldAutoFocus={true}
                    containerRef={this.hintContainerRef}
                  />
                </div>
              )}
            </AnswerContent>
          </HintBox>
          <HoverableAnswerBox>
            <div onClick={this.props.toggleCorrect}>
              <SelectedIcon selected={choice.correct} singleChoice={singleChoice} />
            </div>
            <AnswerContent>
              <ClickableText shouldDisplay={!this.state.editText} onClick={this.toggleEditText}>
                <HTMLRenderer source={this.state.text || "Empty choice"} />
              </ClickableText>
              {this.state.editText && (
                <div ref={this.textContainerRef}>
                  <HTMLEditor
                    ref={this.editorRef}
                    initialContent={this.state.text || ""}
                    currentLocale={currentLocale}
                    getContent={this.getText}
                    setContent={this.editText}
                    shouldAutoSave={this.editorShouldAutoSave("editText")}
                    shouldResetState={this.editorShouldResetState}
                    shouldAutoFocus={true}
                    containerRef={this.textContainerRef}
                  />
                </div>
              )}
            </AnswerContent>
            <ControlBar
              editing={this.state.editText}
              onDelete={this.props.deleteItem}
              onEdit={this.state.editText ? this.saveContent : this.toggleEditText}
            />
          </HoverableAnswerBox>
        </ToggleDisplay>
      </div>
    );
  }

  private editorShouldAutoSave = (editor: "editHint" | "editText") => (
    newProps: IHTMLEditorProps,
    oldProps: IHTMLEditorProps
  ): boolean => {
    return this.state[editor] && newProps.currentLocale !== oldProps.currentLocale;
  };

  private editorShouldResetState = (newProps: IHTMLEditorProps, oldProps: IHTMLEditorProps): boolean => {
    return newProps.initialContent !== oldProps.initialContent || newProps.currentLocale !== oldProps.currentLocale;
  };
}
